import { placeAnchoredMenu, placeSubmenu } from 'BomdisoftComponents/menu/placement';
import { MENU_TAG } from 'BomdisoftComponents/menu/render';

export const MENU_CHOOSE_EVENT = 'bomdisoft-menu-choose';

export const MENU_CLOSE_EVENT = 'bomdisoft-menu-close';

export type MenuAnchorRef = { readonly current: Element | null };

export interface MenuElement extends HTMLElement {
  anchorRef: MenuAnchorRef | null;
  open: boolean;
}

export type MenuView = Window & typeof globalThis;

const VIEWPORT_MARGIN = 8;

const asElement = (target: EventTarget | null): Element | null => (target !== null && typeof (target as Element).closest === 'function' ? (target as Element) : null);

export const createMenuElement = (view: MenuView) =>
  class BomdisoftMenu extends view.HTMLElement implements MenuElement {
    static observedAttributes = ['align', 'anchor-left', 'anchor-top', 'match-anchor-width', 'open', 'placement'];

    #anchorRef: MenuAnchorRef | null = null;
    #isAnnouncedOpen = false;
    #isFollowing = false;

    constructor() {
      super();
      this.addEventListener('toggle', this.#handleToggle);
      this.addEventListener('click', this.#handleClick);
      this.addEventListener('pointerenter', this.#handlePointerEnter, true);
    }

    get anchorRef() {
      return this.#anchorRef;
    }

    set anchorRef(value: MenuAnchorRef | null) {
      this.#anchorRef = value;
      this.#place();
    }

    get open() {
      return this.hasAttribute('open');
    }

    set open(value: boolean) {
      this.toggleAttribute('open', value);
    }

    attributeChangedCallback(name: string) {
      if (name === 'open') this.#sync();
      else this.#place();
    }

    connectedCallback() {
      this.#sync();
    }

    disconnectedCallback() {
      this.#unfollow();
      this.#isAnnouncedOpen = false;
    }

    #isShowing() {
      return typeof this.showPopover === 'function' && this.matches(':popover-open');
    }

    #sync() {
      if (!this.isConnected || typeof this.showPopover !== 'function') return;
      if (!this.open) {
        if (this.#isShowing()) this.hidePopover();
        return;
      }
      if (!this.#isShowing()) this.showPopover();
      this.#isAnnouncedOpen = true;
      this.#place();
      view.requestAnimationFrame(this.#place);
      this.#follow();
    }

    #handleToggle = () => {
      if (this.#isShowing()) {
        this.#markRow(true);
        if (this.open) this.#sync();
        else this.open = true;
        return;
      }
      this.#markRow(false);
      this.#unfollow();
      this.open = false;
      if (!this.#isAnnouncedOpen) return;
      this.#isAnnouncedOpen = false;
      this.dispatchEvent(new view.CustomEvent(MENU_CLOSE_EVENT, { bubbles: true, composed: true }));
    };

    #handleClick = (event: Event) => {
      const row = asElement(event.target)?.closest('.bomdisoft-menu-item');
      if (!row || row.closest(MENU_TAG) !== this || row.hasAttribute('data-submenu') || row.hasAttribute('data-keep-open')) return;
      let fallback: number | undefined;
      const decide = () => {
        view.removeEventListener('click', handleArrival);
        view.clearTimeout(fallback);
        if (!event.defaultPrevented) this.#choose();
      };
      const handleArrival = (arrived: Event) => {
        if (arrived === event) decide();
      };
      view.addEventListener('click', handleArrival);
      fallback = view.setTimeout(decide, 0);
    };

    #handlePointerEnter = (event: Event) => {
      const row = asElement(event.target);
      if (row === null || !row.hasAttribute('data-submenu') || row.hasAttribute('disabled') || row.closest(MENU_TAG) !== this) return;
      const submenu = this.ownerDocument.getElementById(row.getAttribute('popovertarget') ?? '');
      if (submenu instanceof BomdisoftMenu) submenu.open = true;
    };

    #choose() {
      this.dispatchEvent(new view.CustomEvent(MENU_CHOOSE_EVENT, { bubbles: true, composed: true }));
      this.open = false;
      if (this.#isShowing()) this.hidePopover();
      const parent = this.#submenuRow()?.closest(MENU_TAG);
      if (parent instanceof BomdisoftMenu) parent.#choose();
    }

    #submenuRow() {
      const row = this.previousElementSibling;
      return row?.hasAttribute('data-submenu') && row.getAttribute('popovertarget') === this.id ? row : null;
    }

    #markRow(isOpen: boolean) {
      this.#submenuRow()?.setAttribute('aria-expanded', String(isOpen));
    }

    #invoker() {
      for (const candidate of this.ownerDocument.querySelectorAll('[popovertarget]')) if (candidate.getAttribute('popovertarget') === this.id) return candidate;
      return null;
    }

    #place = () => {
      if (!this.#isShowing()) return;
      const pointLeft = this.getAttribute('anchor-left');
      const pointTop = this.getAttribute('anchor-top');
      if (pointLeft !== null && pointTop !== null) {
        this.#placeAtPoint(Number(pointLeft), Number(pointTop));
        return;
      }
      const anchor = this.#anchorRef?.current ?? this.#invoker();
      if (!anchor) return;
      this.style.maxHeight = '';
      const anchorBox = anchor.getBoundingClientRect();
      if (this.hasAttribute('match-anchor-width')) this.style.setProperty('--menu-min-width', `${Math.round(anchorBox.width)}px`);
      const menuBox = this.getBoundingClientRect();
      const viewport = { height: view.innerHeight, width: view.innerWidth };
      const isBeside = this.getAttribute('placement') === 'side';
      const host = isBeside ? anchor.closest('[popover]') : null;
      const bounds = host !== null && host !== this ? host.getBoundingClientRect() : anchorBox;
      const box = { bottom: anchorBox.bottom, height: anchorBox.height, left: bounds.left, right: bounds.right, top: anchorBox.top, width: bounds.width };
      const direction = view.getComputedStyle(anchor).direction === 'rtl' ? 'rtl' : 'ltr';
      const position = isBeside ? placeSubmenu(box, menuBox, viewport) : placeAnchoredMenu(box, menuBox, viewport, { align: this.getAttribute('align') === 'start' ? 'start' : 'center', direction });
      this.style.setProperty('--menu-left', `${position.left}px`);
      this.style.setProperty('--menu-top', `${position.top}px`);
      const originY = position.top >= anchorBox.top ? 'top' : 'bottom';
      const originX = Math.round(Math.min(Math.max(anchorBox.left + anchorBox.width / 2 - position.left, 0), menuBox.width));
      this.style.setProperty('--menu-origin', `${originX}px ${originY}`);
      this.style.maxHeight = position.maxHeight === undefined ? '' : `${position.maxHeight}px`;
    };

    #placeAtPoint(left: number, top: number) {
      const box = this.getBoundingClientRect();
      this.style.setProperty('--menu-left', `${Math.max(VIEWPORT_MARGIN, Math.min(left, view.innerWidth - box.width - VIEWPORT_MARGIN))}px`);
      this.style.setProperty('--menu-top', `${Math.max(VIEWPORT_MARGIN, Math.min(top, view.innerHeight - box.height - VIEWPORT_MARGIN))}px`);
    }

    #follow() {
      if (this.#isFollowing || this.hasAttribute('anchor-left')) return;
      this.#isFollowing = true;
      view.addEventListener('resize', this.#place);
      view.addEventListener('scroll', this.#place, true);
      view.addEventListener('transitionend', this.#place, true);
    }

    #unfollow() {
      if (!this.#isFollowing) return;
      this.#isFollowing = false;
      view.removeEventListener('resize', this.#place);
      view.removeEventListener('scroll', this.#place, true);
      view.removeEventListener('transitionend', this.#place, true);
    }
  };

export const defineMenu = (view: MenuView | null = typeof window === 'undefined' ? null : window) => {
  if (view?.customElements === undefined || view.customElements.get(MENU_TAG) !== undefined) return;
  view.customElements.define(MENU_TAG, createMenuElement(view));
};
